/**
 * Iter 249 — Widgets used in the Commercial Panel.
 * Each widget is a self-contained card that fetches its own data and refreshes.
 * The widget exports a single component that receives `api` (axios) prop.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import {
  Languages, UserCircle, Kanban, ListTodo, Calendar, Search, TrendingUp,
  Loader2, ArrowRight, Phone, Mail, Building2, Sparkles, CheckCircle2,
  Radio, Flame, Bell, StickyNote, MessagesSquare, CalendarDays, BookOpenCheck,
} from "lucide-react";

const WIDGET_ICONS = {
  Languages, UserCircle, Kanban, ListTodo, Calendar, Search, TrendingUp,
  Radio, Flame, Bell, StickyNote, MessagesSquare, CalendarDays, BookOpenCheck,
};

export function getWidgetIcon(name) {
  return WIDGET_ICONS[name] || Languages;
}

// ─── Shared shell ──────────────────────────────────────────────────────

export function WidgetShell({ title, icon: Icon, action, children, testid }) {
  return (
    <div className="h-full bg-[#14101c] border border-white/5 rounded-xl flex flex-col overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 cursor-move drag-handle bg-white/[0.02]">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
          <span className="text-sm font-bold text-white">{title}</span>
        </div>
        {action}
      </div>
      <div className="flex-1 overflow-y-auto p-3">{children}</div>
    </div>
  );
}

// ─── Active Client widget ──────────────────────────────────────────────

export function ActiveClientWidget({ api }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/active-client").then((r) => {
      if (!cancelled) setData(r.data);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  if (loading) return <WidgetShell title="Fiche client active" icon={UserCircle} testid="widget-active-client"><Spinner /></WidgetShell>;
  const p = data?.prospect;
  if (!p) {
    return (
      <WidgetShell title="Fiche client active" icon={UserCircle} testid="widget-active-client">
        <EmptyState label="Aucun client lié récemment" hint="Liez un client à votre prochaine session traduite" />
      </WidgetShell>
    );
  }
  const lastActivity = p.activities && p.activities[p.activities.length - 1];
  return (
    <WidgetShell
      title="Fiche client active"
      icon={UserCircle}
      action={<Link to={`/admin/prospects/${p.prospect_id}`} className="text-xs text-cyan-400 hover:underline flex items-center gap-1">Ouvrir <ArrowRight className="w-3 h-3" /></Link>}
      testid="widget-active-client"
    >
      <div className="space-y-3">
        <div>
          <div className="text-lg font-bold text-white">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}</div>
          {p.company && (
            <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
              <Building2 className="w-3 h-3" /> {p.company}
            </div>
          )}
        </div>
        <div className="space-y-1 text-xs text-slate-300">
          {p.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-500" /> {p.phone}</div>}
          {p.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-500" /> {p.email}</div>}
        </div>
        {lastActivity && lastActivity.summary && (
          <div className="text-[11px] bg-purple-500/5 border border-purple-500/20 rounded p-2 text-purple-100">
            <div className="text-[10px] uppercase text-purple-300 mb-1 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" /> Dernier résumé IA
            </div>
            {lastActivity.summary.slice(0, 200)}{lastActivity.summary.length > 200 ? "…" : ""}
          </div>
        )}
        {p.tags && p.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {p.tags.slice(0, 4).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 border border-white/5">{t}</span>
            ))}
          </div>
        )}
      </div>
    </WidgetShell>
  );
}

// ─── Today Tasks widget ───────────────────────────────────────────────

export function TodayTasksWidget({ api }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/today-tasks").then((r) => {
      if (!cancelled) setItems(r.data.items || []);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);
  return (
    <WidgetShell title="Mes tâches du jour" icon={ListTodo} testid="widget-today-tasks">
      {loading ? <Spinner /> :
        items.length === 0 ? <EmptyState label="Aucune tâche en cours" hint="Profitez-en pour prospecter !" /> :
        <ul className="space-y-1.5">
          {items.map((t) => (
            <li key={t.task_id || t._id || t.title} className="text-xs text-slate-300 flex items-start gap-2 p-1.5 hover:bg-white/[0.02] rounded">
              <CheckCircle2 className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-white truncate">{t.title || t.name || "—"}</div>
                {t.due_date && <div className="text-[10px] text-amber-400 mt-0.5">{formatDate(t.due_date)}</div>}
              </div>
            </li>
          ))}
        </ul>
      }
    </WidgetShell>
  );
}

// ─── Next Appointments widget ─────────────────────────────────────────

export function NextAppointmentsWidget({ api }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/next-appointments").then((r) => {
      if (!cancelled) setItems(r.data.items || []);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);
  return (
    <WidgetShell title="Prochains RDV" icon={Calendar} testid="widget-next-appointments">
      {loading ? <Spinner /> :
        items.length === 0 ? <EmptyState label="Aucun RDV à venir" /> :
        <ul className="space-y-2">
          {items.map((e) => (
            <li key={e.event_id || e._id || e.title} className="text-xs">
              <div className="text-white font-medium truncate">{e.title || "Sans titre"}</div>
              <div className="text-[10px] text-emerald-400">{formatDateTime(e.start_at)}</div>
            </li>
          ))}
        </ul>
      }
    </WidgetShell>
  );
}

// ─── Pipeline Kanban widget ───────────────────────────────────────────

export function PipelineKanbanWidget({ api }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/pipeline-kanban").then((r) => {
      if (!cancelled) setData(r.data);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);
  const stageLabels = {
    prospection: "Prospection", qualification: "Qualification",
    proposition: "Proposition", negociation: "Négociation",
    gagne: "Gagné", perdu: "Perdu",
  };
  return (
    <WidgetShell
      title="Pipeline opportunités"
      icon={Kanban}
      action={<span className="text-[11px] text-slate-400">{data?.total || 0} opportunités</span>}
      testid="widget-pipeline-kanban"
    >
      {loading ? <Spinner /> :
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {Object.entries(data?.stages || {}).map(([st, val]) => (
            <div key={st} className="bg-white/[0.02] border border-white/5 rounded p-2 min-h-[80px]">
              <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">{stageLabels[st] || st}</div>
              <div className="text-lg font-bold text-cyan-400">{val.count}</div>
              <div className="space-y-0.5 mt-1">
                {(val.items || []).slice(0, 3).map((opp) => (
                  <div key={opp.opportunity_id} className="text-[10px] text-slate-300 truncate">{opp.name || "—"}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      }
    </WidgetShell>
  );
}

// ─── Quick Prospect Search widget ─────────────────────────────────────

export function QuickProspectSearchWidget({ api }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const doSearch = async () => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get(`/prospects?search=${encodeURIComponent(q)}`);
      setResults((r.data || []).slice(0, 6));
    } finally { setSearching(false); }
  };

  return (
    <WidgetShell title="Recherche rapide" icon={Search} testid="widget-quick-search">
      <div className="space-y-2">
        <div className="flex gap-1">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder="Nom, email, téléphone…"
            className="flex-1 bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-xs text-white"
            data-testid="quick-search-input"
          />
          <Button onClick={doSearch} size="sm" disabled={searching} data-testid="quick-search-go">
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
        </div>
        {results.length > 0 ? (
          <ul className="space-y-1">
            {results.map((p) => (
              <li key={p.prospect_id}>
                <Link to={`/admin/prospects/${p.prospect_id}`} className="block px-2 py-1.5 hover:bg-white/5 rounded text-xs">
                  <div className="text-white truncate">{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "—"}</div>
                  <div className="text-[10px] text-slate-500 truncate">{p.email || p.phone || p.company}</div>
                </Link>
              </li>
            ))}
          </ul>
        ) : q && !searching ? (
          <div className="text-xs text-slate-500 text-center py-3">Aucun résultat</div>
        ) : (
          <div className="text-[10px] text-slate-600 text-center py-3">Tapez Entrée pour chercher</div>
        )}
        <Link to="/admin/prospects/new" className="block text-center text-[11px] text-cyan-400 hover:underline pt-1">
          + Créer un nouveau prospect
        </Link>
      </div>
    </WidgetShell>
  );
}

// ─── Performance KPIs widget ──────────────────────────────────────────

export function PerformanceKPIsWidget({ api }) {
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/performance-kpis").then((r) => {
      if (!cancelled) setD(r.data);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);
  if (loading) return <WidgetShell title="Mes performances (7 jours)" icon={TrendingUp} testid="widget-performance-kpis"><Spinner /></WidgetShell>;
  const kpis = [
    { label: "Appels traduits", value: d?.translator_sessions || 0, color: "text-cyan-400" },
    { label: "Nouveaux prospects", value: d?.new_prospects || 0, color: "text-purple-400" },
    { label: "Opportunités gagnées", value: d?.won_opportunities || 0, color: "text-emerald-400" },
    { label: "Revenu réalisé", value: `${(d?.revenue_eur || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`, color: "text-amber-400" },
  ];
  return (
    <WidgetShell title="Mes performances (7 jours)" icon={TrendingUp} testid="widget-performance-kpis">
      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white/[0.02] border border-white/5 rounded p-2">
            <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

// ─── Translator Live widget (compact shortcut) ────────────────────────

export function TranslatorLiveWidget() {
  return (
    <WidgetShell title="Traducteur en direct" icon={Languages} testid="widget-translator-live">
      <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-4">
        <div className="w-14 h-14 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center">
          <Languages className="w-7 h-7" />
        </div>
        <div>
          <div className="text-sm text-white font-medium mb-1">Lancez une session FR ↔ EN</div>
          <div className="text-[11px] text-slate-400 max-w-[260px]">
            Capturez le micro ou le son d'un onglet, et obtenez la traduction + le résumé IA en direct.
          </div>
        </div>
        <Link to="/admin/aircall/translator">
          <Button className="bg-purple-500 hover:bg-purple-400 text-white" size="sm" data-testid="widget-translator-launch">
            Ouvrir le traducteur →
          </Button>
        </Link>
      </div>
    </WidgetShell>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function Spinner() {
  return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;
}

function EmptyState({ label, hint }) {
  return (
    <div className="text-center py-4">
      <div className="text-xs text-slate-400">{label}</div>
      {hint && <div className="text-[10px] text-slate-600 mt-1">{hint}</div>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); }
  catch { return iso.slice(0, 10); }
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16); }
}

// ─── Registry: widget id → React component ────────────────────────────

import {
  TranscriptLiveWidget,
  HotLeadsWidget,
  RecentNotificationsWidget,
  QuickNotesWidget,
} from "./widgets_extra";
import {
  MetaInboxPreviewWidget,
  AgendaMiniWidget,
  ObjectionCookbookTopWidget,
} from "./widgets_iter251";

export const WIDGET_COMPONENTS = {
  translator_live: TranslatorLiveWidget,
  transcript_live: TranscriptLiveWidget,
  active_client: ActiveClientWidget,
  hot_leads: HotLeadsWidget,
  pipeline_kanban: PipelineKanbanWidget,
  today_tasks: TodayTasksWidget,
  next_appointments: NextAppointmentsWidget,
  quick_prospect_search: QuickProspectSearchWidget,
  performance_kpis: PerformanceKPIsWidget,
  recent_notifications: RecentNotificationsWidget,
  quick_notes: QuickNotesWidget,
  meta_inbox_preview: MetaInboxPreviewWidget,
  agenda_mini: AgendaMiniWidget,
  objection_cookbook_top: ObjectionCookbookTopWidget,
};
